import dayjs from "dayjs";
import type { SeriesLineOptions } from "highcharts";

import type { SimplifiedSeries } from "@similarweb/pro-logic-components";

import type { ICategoryComparison } from "app/pages/industry-analysis/categories-context/CategoriesContext";

import type { MetricKey, TimeGranularity, PeriodOverPeriodDataPoint, CategoryComparisonData } from "../../types";
import { METRIC_CONFIGURATION } from "../config";

type TimePeriod = "current" | "previous";

/**
 * Creates a standardized metric structure from Period-over-Period API response data.
 *
 * POP endpoints return data in a different format than regular endpoints:
 * - Regular: { Monthly: { Graph: [...] }, Weekly: { Graph: [...] }, Daily: { Graph: [...] } }
 * - POP: Array of { Values: [previousPeriod, currentPeriod], Change: [...] }
 *
 * This function transforms the POP array format into the expected nested structure
 * that the chart component can consume.
 *
 * @param metricData - Array of data points from POP API response
 * @param period - Which time period to extract data for ("current" or "previous")
 * @returns Standardized metric structure with Monthly/Weekly/Daily granularities
 */
export const createMetricFromPopData = (metricData: PeriodOverPeriodDataPoint[], period: TimePeriod) => {
    const periodIndex = period === "previous" ? 0 : 1;
    return {
        Monthly: {
            Graph: metricData.map((item) => ({
                Key: item.Values?.[periodIndex]?.Key || "",
                Value: item.Values?.[periodIndex]?.Value || 0,
                // Include change data and original previous period date for POP tooltips
                ...(period === "current" && {
                    change: Array.isArray(item.Change) ? item.Change[0] || 0 : item.Change || 0,
                    originalPreviousDate: item.Values?.[0]?.Key || "",
                }),
            })),
            Average: 0,
        },
        Weekly: { Graph: [], Average: 0 },
        Daily: { Graph: [], Average: 0 },
    };
};

type FormatterFunction = (value: number) => string;

export const getYAxisFormatter = (selectedMetric: MetricKey): FormatterFunction => {
    const config = METRIC_CONFIGURATION[selectedMetric];
    return config?.yAxisFormatter || ((value: number) => value.toString());
};

const getTooltipFormatter = (selectedMetric: MetricKey): FormatterFunction => {
    const config = METRIC_CONFIGURATION[selectedMetric];
    return config?.tooltipFormatter || ((value: number) => value.toString());
};

export const processStructuredChartData = (
    categoriesData: CategoryComparisonData[],
    selectedMetric: MetricKey,
    timeGranularity: TimeGranularity,
    visibleCategoryIds: Set<string>,
    categories: ICategoryComparison[],
): SimplifiedSeries<SeriesLineOptions> => {
    const config = METRIC_CONFIGURATION[selectedMetric];

    if (!config) {
        return [];
    }

    const series: SimplifiedSeries<SeriesLineOptions> = [];

    categoriesData.forEach(({ category, previousPeriod }) => {
        if (!visibleCategoryIds.has(category.id)) {
            return;
        }

        const categoryComparison = categories.find((cat) => (cat.category.text || cat.category.name) === category.name);
        const color = categoryComparison?.color || "#000000"; // fallback color

        const currentData = { ...category.metrics, id: category.id, name: category.name, color };
        const currentChartPoints = config.dataPath(currentData, timeGranularity);

        series.push({
            id: `${category.id}-current`,
            name: category.name,
            color,
            dashStyle: "Solid",
            data: currentChartPoints.map((point) => ({
                x: dayjs.utc(point.Key).valueOf(),
                y: point.Value,
                change: (point as any).change,
                originalPreviousDate: (point as any).originalPreviousDate,
            })),
        });

        if (previousPeriod) {
            const previousData = {
                ...previousPeriod.metrics,
                id: previousPeriod.id,
                name: previousPeriod.name,
                color,
            };
            const previousChartPoints = config.dataPath(previousData, timeGranularity);
            series.push({
                id: `${category.id}-previous`,
                name: category.name,
                color,
                dashStyle: "LongDash",
                data: previousChartPoints.map((point) => ({
                    x: dayjs.utc(point.Key).valueOf(),
                    y: point.Value,
                })),
            });
        }
    });

    return series;
};
